import React, { useEffect, useState } from 'react';
import {
    IReportBuilderReport, NEW_REPORT_LABELS,
    ReportTypes,
} from 'views/ReportBuilder/constants';
import { NSMultiSelect, NSTable } from 'bricks';
import { useDealDirectoryOptions } from 'views/DocumentLibrary/hooks';
import { IOptionStringOrNumberValue, IOptionStringValue } from 'bricks/types';
import { DEAL_DOCUMENT_TYPE_OPTIONS, DealDocumentType } from 'views/DocumentLibrary/constants';
import { useGetDocumentLibraryDocuments } from 'views/ReportBuilder/hooks';
import RenderIf from 'components/RenderIf/RenderIf';
import {
    DndContext, closestCenter, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
    arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { IDealDocument } from 'views/DocumentLibrary/types';
import SelectDocumentRow from './SelectDocumentRow';
import ReportSaveCard from '../components/ReportSaveCard/ReportSaveCard';
import { IDocumentLibraryReportAsset } from './types';

export interface IProps {
    report: IReportBuilderReport;
    setNewReports: React.Dispatch<React.SetStateAction<IReportBuilderReport[]>>;
    dealId: number;
}

const DocumentLibraryCardEdit = ({ report, setNewReports, dealId }: IProps) => {
    const [selectedFolders, setSelectedFolders] = useState<IOptionStringOrNumberValue[]>(report.selectedFolders || []);
    const [selectedDocumentTypes, setSelectedDocumentTypes] = useState<IOptionStringValue[]>(report.selectedDocumentTypes || []);
    const [selectedDocuments, setSelectedDocuments] = useState<IDocumentLibraryReportAsset[]>(report.selectedDocuments || []);
    const [documents, setDocuments] = useState<IDealDocument[]>([]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const directoryOptions = useDealDirectoryOptions(dealId, 'root');

    const { data: fetchedDocuments, isLoading: isLoadingDocs } = useGetDocumentLibraryDocuments(dealId, {
        types: selectedDocumentTypes.map((type: IOptionStringValue) => type.value) as DealDocumentType[],
        folders: selectedFolders.map((folder: IOptionStringOrNumberValue) => folder.value),
    }, {
        enabled: !!dealId && !!(selectedFolders.length || selectedDocumentTypes.length),
    });

    useEffect(() => {
        if (fetchedDocuments) setDocuments(fetchedDocuments);
    }, [fetchedDocuments]);

    const handleSave = () => {
        setNewReports(prevReports => {
            const reportIndex = prevReports.findIndex(r => r.reportType === ReportTypes.DOCUMENT_LIBRARY);

            if (reportIndex === -1) {
                return prevReports;
            }

            const updatedReports = [...prevReports];
            updatedReports[reportIndex] = {
                ...prevReports[reportIndex],
                isEditing: false,
                selectedFolders,
                selectedDocumentTypes,
                selectedDocuments,
            };
            return updatedReports;
        });
    };

    const handleCancel = () => {
        setNewReports(prevReports => {
            const reportIndex = prevReports.findIndex(r => r.reportType === ReportTypes.DOCUMENT_LIBRARY);

            if (reportIndex === -1 || !prevReports[reportIndex]) {
                return prevReports;
            }

            const updatedReports = [...prevReports];
            const report = prevReports[reportIndex];

            if (report.selectedProFormaId) {
                updatedReports[reportIndex] = {
                    ...report,
                    isEditing: false,
                };
            } else {
                updatedReports.splice(reportIndex, 1);
            }

            return updatedReports;
        });
    };

    const handleToggleDocument = (event: React.ChangeEvent<HTMLInputElement>, docId:number, docName: string, docUrl: string) => {
        if (event.target.checked) {
            setSelectedDocuments([...selectedDocuments, {
                id: Number(docId), name: docName, s3Url: docUrl, checked: event.target.checked,
            }]);
        } else {
            setSelectedDocuments(selectedDocuments.filter(doc => doc.id !== docId));
        }
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (active && over && active.id !== over.id) {
            const oldIndex = documents.map(doc => doc.id).indexOf(Number(active.id));
            const newIndex = documents.map(doc => doc.id).indexOf(Number(over.id));

            const updatedDocuments = arrayMove(documents, oldIndex, newIndex);
            setDocuments(updatedDocuments);
        }
    };

    return (
        <ReportSaveCard
            header={NEW_REPORT_LABELS.DOCUMENT_LIBRARY}
            description="Export select documents based on folder, type, and/or requisition period"
            handleSave={handleSave}
            handleCancel={handleCancel}
            disableSave={isLoadingDocs || selectedDocuments.length === 0}
        >
            <NSMultiSelect
                componentClassName="mt-0"
                selectName="Select Folder"
                label="Select Folder"
                options={directoryOptions}
                onChange={(options: IOptionStringOrNumberValue[]) => setSelectedFolders(options)}
                value={selectedFolders}
                isMulti
                isClearable={false}
                withSelectAllOption
                placeholder="Select..."
            />
            <NSMultiSelect
                selectName="Document Type"
                label="Document Type"
                options={DEAL_DOCUMENT_TYPE_OPTIONS}
                onChange={(options: IOptionStringValue[]) => setSelectedDocumentTypes(options)}
                value={selectedDocumentTypes}
                isMulti
                isClearable={false}
                withSelectAllOption
                placeholder="Select..."
            />
            <RenderIf isTrue={documents} isLoading={isLoadingDocs}>
                <div style={{ maxHeight: '350px' }} className="overflow-auto mt-3">
                    <NSTable className="NSTable">
                        <tbody>
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                <SortableContext items={documents!} strategy={verticalListSortingStrategy}>
                                    {documents?.map(doc => (
                                        <SelectDocumentRow
                                            key={doc.id}
                                            document={doc}
                                            id={doc.id}
                                            selectedDocuments={selectedDocuments}
                                            handleToggleDocument={handleToggleDocument}
                                        />
                                    ))}
                                </SortableContext>
                            </DndContext>
                        </tbody>
                    </NSTable>
                </div>
            </RenderIf>
        </ReportSaveCard>
    );
};
export default DocumentLibraryCardEdit;
